import { Box, Grid } from "@mui/material";
import { GridColDef, GridEventListener } from "@mui/x-data-grid";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { createSearchParams, useNavigate } from "react-router-dom";
import NoData from "../../../components/UI/NoData";
import Table from "../../../components/UI/Table/Table";
import { Calendar } from "../Schedule/Calendar";
import Type from "../Schedule/Type";
import { IMySchedulesPortal } from "../../../models/my-schedule-portal";
import { ETasyScheduleTypeCode } from "../model";

interface IMySchedulesTableProps {
  data: IMySchedulesPortal[];
  isHistoric: boolean;
}

const TRASNLATION_PATH = "page.schedules.mySchedules.";

const LinkAccessTable: React.FC<IMySchedulesTableProps> = ({
  data,
  isHistoric,
}) => {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const columns: GridColDef<IMySchedulesPortal>[] = [
    {
      field: "calendarDate",
      headerName: t(TRASNLATION_PATH + "date")!,
      flex: 1,
      maxWidth: 150,
      renderCell: ({ row: { calendarStartDate } }) => (
        <Calendar date={calendarStartDate} />
      ),
    },

    {
      field: "pacientName",
      headerName: t(TRASNLATION_PATH + "pacientName")!,
      maxWidth: 250,
      flex: 1,
    },
    {
      field: "descriptionSite",
      headerName: t(TRASNLATION_PATH + "descriptionSite")!,
      minWidth: 200,
      flex: 1,
      renderCell: ({
        row: {
          tasyScheduleTypeCode,
          procedureName,
          physicianNameExecute,
          calendarName,
        },
      }) => {
        let displayValue = "";
        if (tasyScheduleTypeCode === Number(ETasyScheduleTypeCode.EXAM)) {
          const type = t(
            TRASNLATION_PATH + `type.${ETasyScheduleTypeCode.EXAM}`,
          );
          const procedure = procedureName || "-";
          const scheduleRoom = calendarName || "-";

          displayValue = t(TRASNLATION_PATH + "examDescription", {
            type,
            procedure,
            scheduleRoom,
            interpolation: { escapeValue: false },
          });
        } else if (
          tasyScheduleTypeCode === Number(ETasyScheduleTypeCode.CONSULTATION)
        ) {
          const type = t(
            TRASNLATION_PATH + `type.${ETasyScheduleTypeCode.CONSULTATION}`,
          );
          const physicianName = physicianNameExecute || "-";

          displayValue = t(TRASNLATION_PATH + "consultationDescription", {
            type,
            physicianName,
            interpolation: { escapeValue: false },
          });
        }

        const lines = displayValue.split("\n");

        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {lines.map((line, index) => (
              <Box
                key={index}
                sx={{
                  whiteSpace: "wrap",
                  lineHeight: "1.2",
                }}
              >
                {line}
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      field: "calendarStatusName",
      minWidth: 150,
      headerName: t(TRASNLATION_PATH + "status")!,
    },
    {
      field: "tasyScheduleTypeCode",
      headerName: t(TRASNLATION_PATH + "tasySchedulingType")!,
      renderCell: ({ row: { tasyScheduleTypeCode } }) => (
        <Type type={tasyScheduleTypeCode?.toString()} />
      ),
    },
  ];

  const onRowClick: GridEventListener<"rowClick"> = useCallback(
    ({ row }) => {
      const params = {
        historic: String(isHistoric),
      };

      navigate({
        pathname: `schedule/${row.identifier}`,
        ...(isHistoric && { search: `${createSearchParams(params)}` }),
      });
    },
    [navigate, isHistoric],
  );

  return (
    <Box pb={2} height="100%" width="100%">
      <Table
        columns={columns}
        rows={data.map((c) => ({ ...c, id: c.identifier }))}
        rowHeight={150}
        rowAction={onRowClick}
      />

      {data.length === 0 && (
        <Grid container mt={5}>
          <Grid item xs={12} sm={12}>
            <NoData />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default memo(LinkAccessTable);
